import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { IPatient } from '@principle-theorem/principle-core/interfaces';
import { INamedDocument } from '@principle-theorem/shared';
import { type IResolvedAttachment } from '../interaction-timeline-event.component';
import { EmailAttachmentMediaService } from '../email-attachment-media.service';

@Component({
  selector: 'pr-email-attachment-menu',
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule],
  templateUrl: './email-attachment-menu.component.html',
  styleUrl: './email-attachment-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailAttachmentMenuComponent {
  private _emailAttachmentMediaService = inject(EmailAttachmentMediaService);
  private _copiedToPatientFiles = signal(false);

  resolved = input.required<IResolvedAttachment>();
  patient = input<INamedDocument<IPatient>>();

  canCopyToPatientFiles = computed(() => {
    if (this._copiedToPatientFiles()) {
      return false;
    }
    const patient = this.patient();
    const resolved = this.resolved();
    if (!patient) {
      return false;
    }
    return !this._emailAttachmentMediaService.isInPatientFiles(
      resolved.attachment,
      patient
    );
  });

  async download(): Promise<void> {
    const resolved = this.resolved();
    await this._emailAttachmentMediaService.downloadAttachment(
      resolved.media,
      resolved.attachment.name
    );
  }

  async copyToPatientFiles(): Promise<void> {
    const patient = this.patient();
    if (!patient || !this.canCopyToPatientFiles()) {
      return;
    }
    const copied = await this._emailAttachmentMediaService.copyToPatientFiles(
      patient,
      this.resolved()
    );
    this._copiedToPatientFiles.set(copied);
  }
}
